// Feature Included
import {
  PageTemplate,
  PDFFeatureIncluded,
} from "@pages/platform-analysis/components/buttons/export-pdf/model";
import {
  MARGIN_DEFAULT,
  PAGE_BODY_HEIGHT_WITHOUT_HEADING,
  TITLE_HEIGHT,
} from "@pages/platform-analysis/components/buttons/export-pdf/constant";
import { View } from "@react-pdf/renderer";
import { PDFTitleSection } from "@pages/platform-analysis/components/buttons/export-pdf/components/title-section";
import { PDFFeaturesIncludeSection } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/features-include/index";
import { cloneDeep } from "lodash";
import { pagesHandler } from "@pages/platform-analysis/components/buttons/export-pdf/util";
import { AnalysisFeatureIncludeDTO } from "@models/platform-analysis/entities/steps/feature";
import { PDFFeaturesIncludeTableConfigs as Configs } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/features-include/configs";

export const featureIncludedPageHandler = ({
  pages,
  spaceAvailable,
  originalFeatureIncluded,
}: {
  pages: PageTemplate[];
  spaceAvailable: number;
  originalFeatureIncluded?: PDFFeatureIncluded[];
}) => {
  const pageBodyHeight = PAGE_BODY_HEIGHT_WITHOUT_HEADING;
  const isAppend = spaceAvailable > pageBodyHeight / 2;

  spaceAvailable = isAppend ? spaceAvailable : pageBodyHeight;

  const featureIncludedComp = (
    dataSeparated: PDFFeatureIncluded[],
    isContinued?: boolean,
  ) => (
    <View>
      <PDFTitleSection
        title={`Features included in the analysis${
          isContinued ? " (continued)" : ""
        }`}
      />
      <PDFFeaturesIncludeSection featureInclude={dataSeparated} />
    </View>
  );

  let featureIncluded = cloneDeep(originalFeatureIncluded);
  let dataProcessed = processFeatureIncluded(
    spaceAvailable,
    featureIncluded ?? [],
  );

  pagesHandler(
    pages,
    featureIncludedComp(dataProcessed?.dataSeparated),
    isAppend,
  );

  while (dataProcessed.hasContinue) {
    spaceAvailable = pageBodyHeight;
    dataProcessed = processFeatureIncluded(
      spaceAvailable,
      featureIncluded ?? [],
    );
    pagesHandler(
      pages,
      featureIncludedComp(dataProcessed?.dataSeparated, true),
      false,
    );
  }

  return spaceAvailable - dataProcessed.occupyHeight - MARGIN_DEFAULT;
};

const processFeatureIncluded = (
  spaceAvailable: number,
  data: PDFFeatureIncluded[],
) => {
  const additionalHeight = TITLE_HEIGHT + Configs.TableHeaderHeight;

  spaceAvailable -= additionalHeight;

  let remainingHeight = spaceAvailable;

  let occupyHeight = 0;
  let hasContinue: boolean;
  let dataSeparated: PDFFeatureIncluded[] = [];

  for (const feature of data) {
    let heightRow = getFeatureIncludedHeight(feature);

    if (remainingHeight - heightRow > 0) {
      dataSeparated?.push(feature);
      remainingHeight -= heightRow;
      occupyHeight = spaceAvailable - remainingHeight;
    } else {
      occupyHeight = spaceAvailable;
      break;
    }
  }

  if (dataSeparated?.length < data?.length) {
    data?.splice(0, dataSeparated.length);
    hasContinue = true;
  } else {
    hasContinue = false;
  }

  return {
    name: "Features included",
    occupyHeight: occupyHeight,
    dataSeparated: dataSeparated,
    hasContinue: hasContinue,
  };
};

export const getFeatureIncludedHeight = (
  row: AnalysisFeatureIncludeDTO,
): number => {
  let height = Configs.DefaultRowHeight;
  let textLengthPrevious = 0;

  row.features.forEach((feature) => {
    textLengthPrevious +=
      feature.featureName.length + Configs.CommaAndBlankTextLength;

    if (textLengthPrevious >= Configs.DefaultMaxCharsPerLine) {
      textLengthPrevious = feature.featureName.length;
      height += Configs.RowHeightLinesMultiplier;
    }
  });

  return height;
};
